/**
 * Zustand store for the New Deployment multi-step wizard.
 */
import type { InstallTiming } from '@/data/applicationInstall'
import type { ApplicationConfig } from '@/types/application'
import { DEFAULT_TLS_PORT } from '@/data/tlsPort'
import { create } from 'zustand'
import type { Environment, DeploymentStrategy, PipelineType, Product, ProductSize, CloudProvider, AppriseVmConfig } from '@/types'
import { DEFAULT_PRODUCT_LINE_ID, type ProductLineId } from '@/data/productLines'
import { DEFAULT_POOL_TIER, RDS_VM_COUNT_DEFAULT, isPoolTierId, type PoolTierId } from '@/data/sizeCatalog'
import { isAppriseProduct, APPRISE_DEFAULT_SIZE } from '@/utils/apprise'
import { enforceWorkwiseExclusive, type SelectionNotice } from '@/utils/workwise/selection'
import { useWorkwiseDraft } from './workwiseDraft'
import type { TraverseDatabase } from '@/utils/traverseProduction'
import type { InfrastructurePayload } from '@/types/infrastructure'
import { usesPoolTiers } from '@/utils/sizing'

export interface WizardProductSelection {
  product: Product
  size: ProductSize
}

export interface WizardEnvironmentConfig {
  name: string
  description: string
  customer_name: string
  tenant_id: string
  cloud_provider: CloudProvider
  environment: Environment
  // Azure
  azure_region: string
  secondary_region: string
  // IBM Cloud
  ibm_region: string
  ibm_region_secondary: string
  ibm_resource_group: string
  ibm_account_id: string
  ibm_vpc_name: string
  // Common
  strategy: DeploymentStrategy
  pipeline_type: PipelineType
  pipeline_url: string
  auto_fix_enabled: boolean
  // Terraform / pipeline params
  create_public_ip: boolean
  load_balancer_required: boolean
  data_disk_sizes: number[]
  additional_tags: Record<string, string>
  // Ross-specific pipeline params
  customer_number: number | undefined
  app_server_count: number
  create_elastic_pool: boolean
  create_database: boolean
  database_name: string
  bac_url: string
  /** Everything the Infrastructure flow collected in step 3; undefined for IBM and until the flow is complete. */
  infrastructure?: InfrastructurePayload
  /** When the application (ERP) can be installed: right away (no new environment) or after the new environment is built and set up. Set on the Application step. */
  application_install?: InstallTiming
  /** What the Application (ERP) step collected for Traverse: the License Server Manager's details and the set-up script's inputs. */
  application?: ApplicationConfig
  bacpac_file: string
  // Traverse session-host pipeline (deploy-traverse-infrastructure.yml)
  traverse: {
    customer_name: string
    /** The product line (see data/productLines.ts). What the pipeline is sent for it is `pipelineValueOf(product_line)`. */
    product_line: ProductLineId
    /** 3-letter Customer ID, uppercase. Derives comp_id (main DB) and sysdb (SYS + ID). */
    customer_id: string
    comp_id: string
    sysdb: string
    tls_port: string
    env_type: 'Production' | 'Sandbox'
    resource_group_name: string
    virtual_network_name: string
    subnet_name: string
    nsg_name: string
    /** The pipeline's name for the number of RDS VMs. Not entered here: it follows the RDS VM count of the Infrastructure flow (NewDeploymentPage). */
    session_host_count: number
    create_public_ip: boolean
    /** Production only; undefined for every other environment. */
    database?: TraverseDatabase
  }
  // Ross app-deploy params (Azure DevOps pipeline 1522)
  ross_app: {
    tenant_id: string       // required by pipeline 1522 (appcentral instance)
    coid: string            // required by pipeline 1522 (appcentral instance)
    customer_id: string
    customer_domain: string
    vm_name: string
    erp_version: string
    iaf_version: string
    install_cfb: boolean
    install_scf: boolean
    sp_url: string
  }
  // Apprise-on-VM config (pipeline_config.apprise_vm), committed on step 3
  apprise_vm: AppriseVmConfig | null
}

interface DeploymentWizardState {
  currentStep: number
  selectedProducts: WizardProductSelection[]
  /**
   * The RDS pool tier, the only size the user chooses, kept for every later page like the product is. Every RDS VM is the same whatever the
   * tier is, and the DB VM is a fixed constant, so neither is stored. Product lines carry this tier as their own `size`.
   */
  poolTier: PoolTierId
  // Set when WorkWise exclusivity just cleared part of the selection.
  selectionNotice: SelectionNotice | null
  environmentConfig: WizardEnvironmentConfig

  setStep: (step: number) => void
  nextStep: () => void
  prevStep: () => void

  toggleProduct: (product: Product) => void
  toggleProductGroup: (parent: Product, children: Product[]) => void
  setProductSize: (productId: number, size: ProductSize) => void
  setPoolTier: (tier: PoolTierId) => void

  setEnvironmentConfig: (config: Partial<WizardEnvironmentConfig>) => void

  reset: () => void
}

const defaultEnvironmentConfig: WizardEnvironmentConfig = {
  name: '',
  description: '',
  customer_name: '',
  tenant_id: '',
  cloud_provider: 'azure',
  environment: 'stg',
  // Azure defaults
  azure_region: 'East US 2',
  secondary_region: '',
  // IBM Cloud defaults
  ibm_region: 'us-south',
  ibm_region_secondary: '',
  ibm_resource_group: 'default',
  ibm_account_id: '',
  ibm_vpc_name: '',
  // Common
  strategy: 'rolling',
  pipeline_type: 'github_actions',
  pipeline_url: '',
  auto_fix_enabled: true,
  // Terraform / pipeline params
  create_public_ip: true,
  load_balancer_required: false,
  data_disk_sizes: [],
  additional_tags: {},
  // Ross-specific pipeline params
  customer_number: undefined,
  app_server_count: 2,
  create_elastic_pool: true,
  create_database: true,
  database_name: 'RossApp',
  bac_url: '',
  bacpac_file: '',
  traverse: {
    customer_name: '',
    product_line: DEFAULT_PRODUCT_LINE_ID,
    customer_id: '',
    comp_id: '',
    sysdb: '',
    tls_port: String(DEFAULT_TLS_PORT),
    env_type: 'Sandbox',
    resource_group_name: '',
    virtual_network_name: '',
    subnet_name: '',
    nsg_name: '',
    session_host_count: RDS_VM_COUNT_DEFAULT,
    create_public_ip: false,
  },
  ross_app: {
    tenant_id: '',
    coid: '',
    customer_id: 'cid0001',
    customer_domain: '',
    vm_name: '',
    erp_version: 'ERP81GA',
    iaf_version: 'IAF10.0',
    install_cfb: true,
    install_scf: false,
    sp_url: 'https://docconnect1.rosserp.apteancloud.com:20100/',
  },
  apprise_vm: null,
}

/** The size a product starts with: Small for Apprise, the shared pool tier for a product line, Medium for any other. */
const startingSize = (product: Product, poolTier: PoolTierId): ProductSize =>
  isAppriseProduct(product) ? APPRISE_DEFAULT_SIZE : usesPoolTiers(product) ? poolTier : 'M'

export const useDeploymentWizard = create<DeploymentWizardState>((set) => ({
  currentStep: 1,
  selectedProducts: [],
  poolTier: DEFAULT_POOL_TIER,
  selectionNotice: null,
  environmentConfig: { ...defaultEnvironmentConfig },

  setStep: (step) => set({ currentStep: step }),
  nextStep: () => set((state) => ({ currentStep: state.currentStep + 1 })),
  prevStep: () => set((state) => ({ currentStep: Math.max(1, state.currentStep - 1) })),

  toggleProduct: (product) =>
    set((state) => {
      const exists = state.selectedProducts.find((p) => p.product.id === product.id)
      const next = exists
        ? state.selectedProducts.filter((p) => p.product.id !== product.id)
        : [...state.selectedProducts, { product, size: startingSize(product, state.poolTier) }]
      const { selection, notice } = enforceWorkwiseExclusive(state.selectedProducts, next)
      return { selectedProducts: selection, selectionNotice: notice }
    }),

  // Toggle entire group: if parent has children, toggle all children (not the parent itself).
  // If no children, toggle the parent product.
  toggleProductGroup: (parent, children) =>
    set((state) => {
      const targets = children.length > 0 ? children : [parent]
      const allSelected = targets.every((t) =>
        state.selectedProducts.some((s) => s.product.id === t.id)
      )
      let next: WizardProductSelection[]
      if (allSelected) {
        // Deselect all
        const ids = new Set(targets.map((t) => t.id))
        next = state.selectedProducts.filter((s) => !ids.has(s.product.id))
      } else {
        // Select missing ones
        const existing = new Set(state.selectedProducts.map((s) => s.product.id))
        const toAdd = targets.filter((t) => !existing.has(t.id)).map((t) => ({ product: t, size: startingSize(t, state.poolTier) }))
        next = [...state.selectedProducts, ...toAdd]
      }
      const { selection, notice } = enforceWorkwiseExclusive(state.selectedProducts, next)
      return { selectedProducts: selection, selectionNotice: notice }
    }),

  setProductSize: (productId, size) =>
    set((state) => {
      const target = state.selectedProducts.find((p) => p.product.id === productId)
      // A product line's size IS the shared pool tier: choosing it for one line chooses it for every line.
      if (target && usesPoolTiers(target.product) && isPoolTierId(size)) {
        return {
          poolTier: size,
          selectedProducts: state.selectedProducts.map((p) => (usesPoolTiers(p.product) ? { ...p, size } : p)),
        }
      }
      return { selectedProducts: state.selectedProducts.map((p) => (p.product.id === productId ? { ...p, size } : p)) }
    }),

  setPoolTier: (tier) =>
    set((state) => ({
      poolTier: tier,
      selectedProducts: state.selectedProducts.map((p) => (usesPoolTiers(p.product) ? { ...p, size: tier } : p)),
    })),

  setEnvironmentConfig: (config) =>
    set((state) => ({
      environmentConfig: { ...state.environmentConfig, ...config },
    })),

  reset: () => {
    // A fresh wizard session also drops any in-memory WorkWise draft.
    useWorkwiseDraft.getState().clear()
    set({
      currentStep: 1,
      selectedProducts: [],
      poolTier: DEFAULT_POOL_TIER,
      selectionNotice: null,
      environmentConfig: { ...defaultEnvironmentConfig },
    })
  },
}))
